import { ArrowRight } from 'lucide-react';
import Reveal from '@/components/Reveal';
import { showcaseVenues, useEstablishments, type VenueSummary } from '@/lib/establishments';
import { pluralRu } from '@/utils/format';

/**
 * Витрина реальных заведений: статичная стена с названиями из живой базы подключений.
 * Те же записи, что стоят точками на карте ниже.
 */
export default function Venues() {
  const data = useEstablishments();
  const list = showcaseVenues(data);
  if (list.length === 0) return null;

  const cityCount = data.cities.length;

  return (
    <section className="section-tight" aria-labelledby="venues-title">
      <div className="container-site">
        <div className="grid gap-8 md:grid-cols-12 md:items-end">
          <div className="md:col-span-7">
            <Reveal>
              <h2 id="venues-title" className="h2">
                <span className="num">{data.total}</span>{' '}
                {pluralRu(data.total, 'заведение', 'заведения', 'заведений')} в{' '}
                <span className="num">{cityCount}</span>{' '}
                {pluralRu(cityCount, 'городе', 'городах', 'городах')} уже{' '}
                {pluralRu(data.total, 'работает', 'работают', 'работают')} на&nbsp;Resstro
              </h2>
            </Reveal>
          </div>
          <Reveal delay={80} className="md:col-span-5">
            <p className="lead">
              Кофейни, рестораны и доставки, которые каждый день пробивают заказы через Resstro.
              Названия из базы подключений, а не из пресс-кита.
            </p>
            <a
              href="#map"
              className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-medium text-ink transition-colors hover:text-orange"
            >
              Посмотреть на карте
              <ArrowRight size={15} strokeWidth={2} className="text-orange" />
            </a>
          </Reveal>
        </div>

        <Reveal delay={120}>
          <ul className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:mt-14 sm:grid-cols-3 lg:grid-cols-4">
            {list.map((v) => (
              <VenueCell key={v.id} venue={v} />
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

function VenueCell({ venue }: { venue: VenueSummary }) {
  return (
    <li className="flex min-w-0 items-center gap-3 bg-white px-4 py-4 sm:px-5">
      {venue.logoUrl ? (
        <img
          src={venue.logoUrl}
          alt=""
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
          className="h-10 w-10 shrink-0 rounded-xl object-cover ring-1 ring-ink/10"
        />
      ) : (
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-canvas text-[14px] font-semibold uppercase text-ink/70 ring-1 ring-ink/10">
          {venue.name.slice(0, 1)}
        </span>
      )}
      <span className="min-w-0">
        <span className="block truncate text-[14px] font-medium leading-tight text-ink">{venue.name}</span>
        <span className="mt-0.5 block truncate text-[12.5px] text-ink/50">{venue.city}</span>
      </span>
    </li>
  );
}
